import PageHeaderAction from '../../../../components/PageHeaderAction';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Form, Space } from 'antd';
import { useCreateData, useFetchData } from '../../../../hooks';
import { useAuthStore } from '../../../../store';
import { BaseModel, FormField, PageableResponse } from '../../../../types';
import { Currency, Resource } from '../../../../lib/enums';
import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { dateFormat } from '../../../../lib/utils';
import ErrorModal from '../../../../components/ui/modal/ErrorModal';
import InfoModal from '../../../../components/ui/modal/InfoModal';
import CustomForm from '../../../../components/ui/CustomForm';
import SubmitButton from '../../../../components/ui/SubmitButton';
import OrderLineFormCreate from './OrderLineFormCreate';
import { usePurchaseOrderStore } from '../../../../store/PurchaseOrderStore';
import { PurchaseOrderForm } from '../../../../types/PurchaseOrder';
import { useUploadPOFiles } from '../../../../hooks/usePurchaseOrderHooks';

const PurchaseOrderFormCreatePage = () => {
  const api = '/purchase/orders';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const purchaseOrderStore = usePurchaseOrderStore();
  const { user } = useAuthStore();
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [errorMess, setErrorMess] = useState<string | undefined>('');
  const [fileList, setFileList] = useState<FormData | null>(null);

  const { data: purchaseTermData, isLoading: isPurchaseTermLoading } =
    useFetchData<PageableResponse<BaseModel>>('/purchase/terms', {
      resource: searchParams.get('resource') || Resource.FULL,
      length: parseInt(searchParams.get('length') || '999999'),
    });

  const { data: vendorData, isLoading: isVendorLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/partner/vendors', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  const { data: defaultLocationData, isLoading: isDefaultLocationLoading } =
    useFetchData<PageableResponse<BaseModel>>('/location/locations', {
      resource: Resource.MIN,
      search: '',
      length: '999999',
    });

  const {
    mutate,
    isPending,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
    data: createdDate,
  } = useCreateData(api);

  const {
    mutate: uploadMutate,
    isError: isErrorUpload,
    error: errorUpload,
  } = useUploadPOFiles();

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
    // router.push('/purchase-order');
  };

  const handleInfoModalClose = () => {
    setIsInfoModalOpen(false);
    navigate('/purchase-order');
  };

  useEffect(() => {
    if (isSuccessCreate) {
      if (fileList !== null) {
        uploadMutate({ id: createdDate.data.id, formData: fileList });
      }
      setIsInfoModalOpen(true);
    }
    if (isErrorUpload) {
      setIsErrorModalOpen(true);
      setErrorMess(errorUpload.message);
    }
    // isErrorCreate must be a dependency: without it a failed save (e.g. the
    // server's 400 "A vendor is required.") never opened this modal and the
    // user sat on an unchanged form.
    if (isErrorCreate) {
      setIsErrorModalOpen(true);
      setErrorMess(
        errorCreate?.response?.data?.message ??
          'Could not save the purchase order.',
      );
    }
  }, [isSuccessCreate, isErrorUpload, isErrorCreate]);

  const currencyOptions: BaseModel[] = Object.keys(Currency).map(
    (key) =>
      ({
        id: key,
        code: Currency[key as keyof typeof Currency],
        description: Currency[key as keyof typeof Currency],
      }) as BaseModel,
  );

  const handleClose = () => {
    navigate('/purchase-order');
  };

  const fields: FormField[] = [
    {
      name: 'orderDate',
      label: 'Order Date',
      rules: [{ required: true, message: 'Please select order date' }],
      type: 'datepicker',
      defaultValue: dayjs(new Date()).format(dateFormat),
    },
    {
      name: 'requiredDate',
      label: 'Required Date',
      rules: [{ required: true, message: 'Please select required date' }],
      type: 'datepicker',
      defaultValue: purchaseOrderStore.formData.requiredDate,
    },
    {
      name: 'source',
      label: 'Source Document',
      type: 'input',
      defaultValue: purchaseOrderStore.formData.source,
    },
    {
      name: 'vendor',
      label: 'Vendor',
      type: 'select',
      isLoading: isVendorLoading,
      isMultiple: false,
      values: vendorData?.data,
      rules: [{ required: true, message: 'Please select vendor' }],
      defaultValue: purchaseOrderStore.formData.vendor?.id,
    },
    {
      name: 'destination',
      label: 'Destination',
      allowClear: true,
      type: 'select',
      isLoading: isDefaultLocationLoading,
      isMultiple: false,
      values: defaultLocationData?.data,
      rules: [{ required: true, message: 'Please select destination' }],
      defaultValue:
        purchaseOrderStore.formData.destination !== undefined &&
        purchaseOrderStore.formData.destination !== null
          ? purchaseOrderStore.formData.destination.id
          : user?.defaultLocation?.id,
    },
    {
      name: 'currency',
      label: 'Currency',
      type: 'radio-group-button',
      defaultValue: purchaseOrderStore.formData.currency,
      values: currencyOptions,
    },
    {
      name: 'terms',
      label: 'Terms',
      type: 'select',
      isMultiple: false,
      isLoading: isPurchaseTermLoading,
      values: purchaseTermData?.data,
      defaultValue: purchaseOrderStore.formData.terms?.id,
    },
    {
      // Was 'Notes'. The DTO field is notes, so every note typed while
      // raising an order went nowhere -- and only on create; editing an
      // existing order has always saved, which is why it was never noticed.
      name: 'notes',
      label: 'Remarks',
      type: 'textarea',
      defaultValue: purchaseOrderStore.formData.notes,
    },
    {
      name: 'status',
      label: 'Status',
      type: 'status',
      defaultValue: 'DRAFT',
    },
    {
      name: 'files',
      label: 'Attachments',
      disabled: false,
      type: 'file',
    },
  ];

  const onFinish = async (values: any) => {
    const vendor: BaseModel | undefined = vendorData?.data.find((obj) => {
      return obj.id == values.vendor;
    });
    const destination: BaseModel | undefined = defaultLocationData?.data.find(
      (obj) => {
        return obj.id == values.destination;
      },
    );

    const terms: BaseModel | undefined = purchaseTermData?.data.find((obj) => {
      return obj.id == values.terms;
    });

    const payload: PurchaseOrderForm = {
      ...values,
      vendor: vendor,
      destination: destination,
      terms: terms,
      orderDate: dayjs(values.orderDate).format(dateFormat),
      requiredDate: dayjs(values.requiredDate).format(dateFormat),
      orderLines: purchaseOrderStore.formData.orderLines || [],
    };
    mutate(payload);
    if (values.files !== undefined) {
      const fileList = values.files.fileList;
      const formData = new FormData();
      fileList.forEach((file: any) => {
        formData.append('files', file.originFileObj);
      });
      setFileList(formData);
    }
  };

  return (
    <>
      <div className={'flex items-center justify-between'}>
        <PageHeaderAction action={'Request'} moduleName={'Purchase Order'} />
        <Space>
          <Button onClick={handleClose}>Close</Button>
          <SubmitButton loading={isPending} form={form}>
            Submit
          </SubmitButton>
        </Space>
      </div>
      <div className="bg-white mt-4 h-[calc(84vh)] overflow-auto rounded-xl">
        <div className="p-4">
          <CustomForm
            data={purchaseOrderStore.formData}
            formFields={fields}
            form={form}
            onFinish={onFinish}
            rowCol={3}
            layout={'vertical'}
            formName={'po_form'}
          />
        </div>
        <OrderLineFormCreate />
      </div>

      <ErrorModal
        visible={isErrorModalOpen}
        message={errorMess}
        onClose={handleErrorModalClose}
      />

      <InfoModal
        visible={isInfoModalOpen}
        message={'Purchase order successfully created'}
        onClose={handleInfoModalClose}
      />
    </>
  );
};

export default PurchaseOrderFormCreatePage;
